// The Canvas `onCreated` hook of Scene: publishes the live scene / camera to
// `window` for the screen audits and the e2e bridge. Pure side effect, no React.

import * as THREE from 'three';
import { HEX_HEIGHT, axialToWorld } from '../HexGrid.js';
import { getBoardScale, scalePoint } from '../boardScale.js';

/** The slice of the OrbitControls ref the e2e `target()` probe reads. */
export interface TargetControlsRef {
  readonly current: { readonly target?: { x: number; y: number; z: number } } | null;
}

export function installSceneGlobals(
  { camera, gl, scene }: { camera: THREE.Camera; gl: THREE.WebGLRenderer; scene: THREE.Scene },
  controlsRef: TargetControlsRef,
): void {
  // Screen audits read the live scene: screen-audit's dual-dom plugin
  // and scripts/audit/ui-invariants.mjs (props buried in terrain, grid
  // lines off the tile edges). Meshes they inspect carry `name`s.
  const w = window as unknown as {
    __GAME_SCENE__?: THREE.Scene;
    __GAME_CAMERA__?: THREE.Camera;
    THREE?: typeof THREE;
  };
  w.__GAME_SCENE__ = scene;
  w.__GAME_CAMERA__ = camera;
  w.THREE = THREE;
  const project = (q: number, r: number, yOffset = HEX_HEIGHT / 2) => {
    const [wx, , wz] = scalePoint(axialToWorld(q, r));
    const worldPos = new THREE.Vector3(wx, yOffset * getBoardScale(), wz);
    const screenPos = worldPos.clone().project(camera);
    const rect = gl.domElement.getBoundingClientRect();
    return {
      x: rect.left + rect.width * (screenPos.x * 0.5 + 0.5),
      y: rect.top + rect.height * (-screenPos.y * 0.5 + 0.5),
    };
  };
  (
    window as unknown as {
      __aodCamera?: {
        project: typeof project;
        world: typeof axialToWorld;
        target?: () => [number, number, number] | null;
      };
    }
  ).__aodCamera = {
    project,
    world: (q: number, r: number) => scalePoint(axialToWorld(q, r)),
    // Live orbit target (rendered space) for the minimap-drag e2e.
    target: () => {
      const t = controlsRef.current?.target;
      return t ? [t.x, t.y, t.z] : null;
    },
  };
}
